import PropTypes from 'prop-types';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { logger } from '@core/utils';
import { Toast, Button } from '@core/components/ui';
import {
  FAQTable,
  FAQFormModal,
  FAQDeleteModal,
} from './index';
import { useFAQOperations } from '../hooks';
import { useCreateFAQMutation, useUpdateFAQMutation } from '../api/faqApi';

/**
 * FAQManager Component
 *
 * Reusable FAQ management component that can be filtered by knowledge base.
 * Displays and manages FAQ entries for general or dedicated knowledge bases.
 *
 * @param {Object} props
 * @param {string} [props.segmentedKnowledgeId] - Optional segment ID to filter FAQs (null = general base)
 * @param {string} props.accountId - Account ID for FAQ creation
 */
export function FAQManager({ segmentedKnowledgeId = null, accountId }) {
  const { t } = useTranslation();

  // Local state for modals and current FAQ
  const [faqToEdit, setFaqToEdit] = useState(null);
  const [faqToDelete, setFaqToDelete] = useState(null);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // RTK Query mutations
  const [createFAQ, { isLoading: isCreating }] = useCreateFAQMutation();
  const [updateFAQ, { isLoading: isUpdating }] = useUpdateFAQMutation();

  // FAQ operations hook with segmentedKnowledgeId filter
  const {
    faqs,
    isLoading,
    deleteFAQ,
    error,
    successMessage,
    clearError,
    clearSuccess,
  } = useFAQOperations({ t, segmentedKnowledgeId });

  // Create handlers
  const handleCreateClick = () => {
    setFaqToEdit(null);
    setFormModalOpen(true);
  };

  // Edit handlers
  const handleEditClick = (faq) => {
    setFaqToEdit(faq);
    setFormModalOpen(true);
  };

  // Delete handlers
  const handleDeleteClick = (faq) => {
    setFaqToDelete(faq);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = useCallback(async (faqId) => {
    await deleteFAQ(faqId);
    setDeleteModalOpen(false);
    setFaqToDelete(null);
  }, [deleteFAQ]);

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setFaqToDelete(null);
  };

  // Form submit handler
  const handleFormSubmit = async (formData) => {
    try {
      if (faqToEdit) {
        // Update existing FAQ
        await updateFAQ({
          id: faqToEdit.id,
          ...formData,
          segmentedKnowledgeId,
          accountId,
        }).unwrap();
      } else {
        // Create new FAQ
        await createFAQ({
          ...formData,
          segmentedKnowledgeId,
          accountId,
        }).unwrap();
      }
      setFormModalOpen(false);
      setFaqToEdit(null);
    } catch (err) {
      logger.error('Form submit error:', err);
    }
  };

  const handleFormCancel = () => {
    setFormModalOpen(false);
    setFaqToEdit(null);
  };

  return (
    <div>
      {/* Create Button */}
      <div className="mb-6 flex justify-end">
        <Button onClick={handleCreateClick}>
          <Icon icon="lucide:plus" className="size-4 mr-1" />
          {t('faq.createNew', 'Create FAQ')}
        </Button>
      </div>

      {/* FAQ Table */}
      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-sm text-muted">
            {t('common.loading', 'Loading...')}
          </p>
        </div>
      ) : (
        <FAQTable
          faqs={faqs}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
        />
      )}

      {/* Form Modal */}
      <FAQFormModal
        isOpen={formModalOpen}
        faq={faqToEdit}
        onClose={handleFormCancel}
        onSubmit={handleFormSubmit}
        isSubmitting={isCreating || isUpdating}
      />

      {/* Delete Confirmation Modal */}
      <FAQDeleteModal
        isOpen={deleteModalOpen}
        faq={faqToDelete}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Error Toast */}
      <Toast
        message={error || ''}
        type="error"
        onClose={clearError}
      />

      {/* Success Toast */}
      <Toast
        message={successMessage || ''}
        type="success"
        onClose={clearSuccess}
      />
    </div>
  );
}

FAQManager.propTypes = {
  segmentedKnowledgeId: PropTypes.string,
  accountId: PropTypes.string.isRequired,
};
